import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, User, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ImpersonationBanner: React.FC = () => {
  const { impersonationContext, stopImpersonation, getImpersonationTimeRemaining } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Update countdown every second
  useEffect(() => {
    if (!impersonationContext.isImpersonating) return;

    const updateCountdown = () => {
      const remainingMs = getImpersonationTimeRemaining();
      if (remainingMs <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const minutes = Math.floor(remainingMs / 1000 / 60);
      const seconds = Math.floor((remainingMs / 1000) % 60);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [impersonationContext.isImpersonating, getImpersonationTimeRemaining]);

  if (!impersonationContext.isImpersonating || !impersonationContext.targetUser || !impersonationContext.originalAdminUser) {
    return null;
  }

  const { targetUser, originalAdminUser } = impersonationContext;

  return (
    <div className="bg-orange-100 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 px-4 py-2">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400" />
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-orange-800 dark:text-orange-200">
              <strong>Admin Mode:</strong> You are impersonating
            </span>
            <div className="flex items-center space-x-1 px-2 py-1 bg-orange-200 dark:bg-orange-800 rounded">
              <User size={12} className="text-orange-700 dark:text-orange-300" />
              <span className="font-medium text-orange-900 dark:text-orange-100">
                {targetUser.name || targetUser.email}
              </span>
            </div>
            <span className="text-orange-700 dark:text-orange-300">
              as admin <strong>{originalAdminUser.name || originalAdminUser.email}</strong>
            </span>
          </div>
        </div>
        
        <button
          onClick={stopImpersonation}
          className="flex items-center space-x-2 px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm font-medium transition-colors"
        >
          <X size={14} />
          <span>Stop Impersonation</span>
          {timeRemaining && (
            <div className="flex items-center space-x-1 px-2 py-0.5 bg-orange-700 rounded">
              <Clock size={12} />
              <span className="font-mono text-xs">
                {timeRemaining}
              </span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;